import { inject, Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class IconsService {
  /**
   * Constructor
   */
  constructor() {
    const domSanitizer = inject(DomSanitizer);
    const matIconRegistry = inject(MatIconRegistry);

    /**
     * Helper function to register icons
     * Security: bypassSecurityTrustResourceUrl is safe here because:
     * 1. Icon names are hardcoded in the application source code
     * 2. Paths are static and point to local assets in the icons/ directory
     * 3. No user input or external data is used in path construction
     * 4. The icon name is validated to contain only safe characters (alphanumeric, hyphens, underscores)
     */
    const registerIcon = (name: string): void => {
      // Validate icon name to prevent path traversal attacks
      if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
        console.error(
          `Invalid icon name: ${name}. Only alphanumeric characters, hyphens, and underscores are allowed.`
        );
        return;
      }
      // Safe: Hardcoded paths to local assets, no user input

      matIconRegistry.addSvgIcon(name, domSanitizer.bypassSecurityTrustResourceUrl(`icons/${name}.svg`));
    };

    /**
     * Register icon sets
     * Security: These paths are hardcoded and point to trusted local assets
     */
    // Safe: Hardcoded path to local asset

    matIconRegistry.addSvgIconSet(domSanitizer.bypassSecurityTrustResourceUrl('icons/material-twotone.svg'));
    matIconRegistry.addSvgIconSetInNamespace(
      'mat_outline',
      // Safe: Hardcoded path to local asset

      domSanitizer.bypassSecurityTrustResourceUrl('icons/material-outline.svg')
    );
    matIconRegistry.addSvgIconSetInNamespace(
      'mat_solid',
      // Safe: Hardcoded path to local asset

      domSanitizer.bypassSecurityTrustResourceUrl('icons/material-solid.svg')
    );
    matIconRegistry.addSvgIconSetInNamespace(
      'feather',
      // Safe: Hardcoded path to local asset

      domSanitizer.bypassSecurityTrustResourceUrl('icons/feather.svg')
    );
    matIconRegistry.addSvgIconSetInNamespace(
      'heroicons_outline',
      // Safe: Hardcoded path to local asset

      domSanitizer.bypassSecurityTrustResourceUrl('icons/heroicons-outline.svg')
    );
    matIconRegistry.addSvgIconSetInNamespace(
      'heroicons_solid',
      // Safe: Hardcoded path to local asset

      domSanitizer.bypassSecurityTrustResourceUrl('icons/heroicons-solid.svg')
    );
    matIconRegistry.addSvgIconSetInNamespace(
      'heroicons_mini',
      // Safe: Hardcoded path to local asset

      domSanitizer.bypassSecurityTrustResourceUrl('icons/heroicons-mini.svg')
    );

    // Register icons using helper function
    const icons = [
      'alert-error',
      'alert-info',
      'ankle',
      'appointment',
      'arrow-down',
      'arrow-left',
      'arrow-right',
      'back-arrow',
      'calendar-outline',
      'calendar',
      'check-x-fill',
      'check-x',
      'check',
      'checkmark-green',
      'checkmark-white',
      'checkmark',
      'chevron-down',
      'chevron-left',
      'chevron-right',
      'chevron-up',
      'clinical-data',
      'close',
      'collapse-icon',
      'diskette',
      'dns',
      'preview',
      'sidebar',
      'alert-info',
      // Alert popup header icons (public/icons/*.svg)
      'unsaved-changes-warning',
      'change-survey-type-warning',
      'trash-info',
      'plus-outline',
      'table-sort-icon',
      'arrow-left',
      'arrow-right',
      'arrow-down',
      'filter-icon',
      'form',
      'dropdown-arrow',
      'checkmark',
      'checkmark-green',
      'checkmark-white',
      'search-icon',
      'calendar-outline',
      'resend-email',
      'email-method',
      'phone',
      'rtp',
      'sms-error',
      'sms-method',
      'sms-email-method',
      'preferred-method',
      'document-text',
      'download',
      'download-pdf',
      'download-xml',
      'download-xlsx',
      'dropdown-arrow',
      'email-method',
      'filter-icon',
      'form',
      'fullscreen',
      'funnel',
      'hand',
      'hip',
      'joint',
      'knee',
      'logout',
      'note',
      'pagination-next',
      'pagination-prev',
      'patient-survey',
      'patient',
      'phone',
      'pin-off',
      'pin',
      'plus-outline',
      'preferred-method',
      'preview',
      'reception',
      'reports',
      'resend-email',
      'rtp',
      'save',
      'score-card',
      'search-icon',
      'settings',
      'shoulder',
      'sidebar',
      'sms-email-method',
      'sms-error',
      'sms-method',
      'spine',
      'star',
      'table-sort-icon',
      'tag',
      'trash-info',
      'unsaved-changes-warning',
      'trash',
    ];

    icons.forEach(registerIcon);
  }
}
